import {
  AppShell,
  BrandLogo,
  ElvaPageLoader,
  GROWTH_ACCENT_BAR,
  Segmented,
} from '@elva/ui';
import { LocationWorkspaceSwitcher, useAuthStore } from '@elva/platform';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  getModuleById,
  getModuleFromPath,
  getNavKeyFromPath,
  GROWTH_MODULES,
  NAV_PATHS,
  type GrowthModuleId,
} from '@/app/config/nav';
import { GrowthModuleCard } from '@/app/layouts/GrowthModuleCard';
import { PageTopBarProvider, usePageTopBarContext } from '@/app/layouts/pageTopBar';
import { useLocationInvalidation } from '@/data/useLocationInvalidation';
import { useLocationScope } from '@/data/useLocationScope';

function initialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0];
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1];
  return `${first[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

function AppLayoutShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const active = getNavKeyFromPath(location.pathname);
  const activeModule = getModuleFromPath(location.pathname);
  const { isLocationReady } = useLocationScope();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { actionsRef, actionsTick } = usePageTopBarContext();

  useLocationInvalidation();

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const displayName = user
    ? user.name.trim() || (user.email.split('@')[0] ?? 'Account')
    : 'Account';
  const roleLabel =
    user?.role && typeof user.role === 'object' && user.role.name
      ? user.role.name
      : 'Growth';

  const topBarActions = actionsTick >= 0 ? actionsRef.current : null;

  return (
    <AppShell
      accentBar={GROWTH_ACCENT_BAR}
      sidebarCollapsed={sidebarCollapsed}
      onSidebarCollapsedChange={setSidebarCollapsed}
      sidebar={{
        brand: {
          name: 'Growth',
          badge: 'Elva',
          logo: <BrandLogo src="/logo.svg" alt="Elva" />,
        },
        productSlot: (
          <GrowthModuleCard
            label={activeModule.label}
            subtitle={activeModule.subtitle}
            icon={activeModule.icon}
            collapsed={sidebarCollapsed}
          />
        ),
        nav: activeModule.nav,
        navSectionLabel: 'Navigate',
        active,
        onNav: (key) => {
          const path = NAV_PATHS[key];
          if (path) void navigate(path);
        },
        user: {
          name: displayName,
          role: roleLabel,
          initials: initialsFromName(displayName),
          onLogout: () => {
            void logout().catch(() => undefined);
          },
        },
      }}
      topbar={{
        title: (
          <Segmented
            value={activeModule.id}
            onChange={(value) => {
              const module = getModuleById(value as GrowthModuleId);
              void navigate(module.defaultPath);
            }}
            options={GROWTH_MODULES.map((module) => ({
              value: module.id,
              label: module.label,
              icon: module.icon,
            }))}
          />
        ),
        actions: (
          <>
            <LocationWorkspaceSwitcher placement="header" />
            {topBarActions}
          </>
        ),
      }}
      fill={location.pathname.startsWith('/settings/')}
    >
      {isLocationReady ? <Outlet key={location.pathname} /> : <ElvaPageLoader />}
    </AppShell>
  );
}

export function AppLayout() {
  return (
    <PageTopBarProvider>
      <AppLayoutShell />
    </PageTopBarProvider>
  );
}
